"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Modal, ModalHeader, ModalBody, Button, Card, CardBody, Badge, Row, Col } from "reactstrap"
import { FaInfoCircle, FaCheckCircle, FaExclamationTriangle, FaFileInvoiceDollar, FaFileAlt } from "react-icons/fa"

interface VerificationGuideProps {
  isOpen: boolean
  toggle: () => void
}

const VerificationGuideModal: React.FC<VerificationGuideProps> = ({ isOpen, toggle }) => {
  const [dontShowAgain, setDontShowAgain] = useState(false)

  useEffect(() => {
    const savedPreference = localStorage.getItem("hideVerificationGuide")
    if (savedPreference === "true") {
      setDontShowAgain(true)
    }
  }, [])

  const handleDontShowAgain = () => {
    const newValue = !dontShowAgain
    setDontShowAgain(newValue)
    localStorage.setItem("hideVerificationGuide", newValue.toString())
  }

  return (
    <Modal
      isOpen={isOpen && !dontShowAgain}
      toggle={toggle}
      size="lg"
      centered
      className="verification-guide-modal"
      style={{ maxWidth: "600px" }}
    >
      <ModalHeader toggle={toggle} className="py-2 px-3 bg-light">
        <div className="d-flex align-items-center">
          <FaInfoCircle className="text-primary me-2" size={16} />
          <span className="fw-bold">VERIFICATION GUIDE</span>
        </div>
      </ModalHeader>

      <ModalBody className="p-3">
        <Row className="g-2">
          {/* Free Orders Card */}
          <Col md={6}>
            <Card className="border-success h-100">
              <CardBody className="p-3">
                <div className="d-flex align-items-center mb-2">
                  <div className="rounded-circle bg-success p-2 me-2">
                    <FaCheckCircle className="text-white" size={12} />
                  </div>
                  <Badge color="success" className="fw-bold">
                    FREE ORDERS
                  </Badge>
                </div>
                <ul className="small mb-0 ps-3">
                  <li>Zero amount - approve directly</li>
                  <li>Verify exhibitor details</li>
                  <li>Check representative limit</li>
                  <li>✅ Can approve immediately</li>
                </ul>
              </CardBody>
            </Card>
          </Col>

          {/* Paid Orders Card */}
          <Col md={6}>
            <Card className="border-info h-100">
              <CardBody className="p-3">
                <div className="d-flex align-items-center mb-2">
                  <div className="rounded-circle bg-info p-2 me-2">
                    <FaFileInvoiceDollar className="text-white" size={12} />
                  </div>
                  <Badge color="info" className="fw-bold">
                    PAID ORDERS
                  </Badge>
                </div>
                <ul className="small mb-0 ps-3">
                  <li>Verify payment amount matches</li>
                  <li>Check proof authenticity</li>
                  <li>Confirm transaction details</li>
                  <li>✅ Approve after verification</li>
                </ul>
              </CardBody>
            </Card>
          </Col>

          {/* Pending Orders Card */}
          <Col md={6}>
            <Card className="border-warning h-100">
              <CardBody className="p-3">
                <div className="d-flex align-items-center mb-2">
                  <div className="rounded-circle bg-warning p-2 me-2">
                    <FaExclamationTriangle className="text-white" size={12} />
                  </div>
                  <Badge color="warning" className="fw-bold">
                    PENDING ORDERS
                  </Badge>
                </div>
                <ul className="small mb-0 ps-3">
                  <li>
                    <strong className="text-danger">❌ Don't approve</strong> if payment needed
                  </li>
                  <li>Approve button is hidden</li>
                  <li>Contact exhibitor if delayed</li>
                  <li>Wait for payment submission</li>
                </ul>
              </CardBody>
            </Card>
          </Col>

          {/* Postpaid Orders Card */}
          <Col md={6}>
            <Card className="border-secondary h-100">
              <CardBody className="p-3">
                <div className="d-flex align-items-center mb-2">
                  <div className="rounded-circle bg-secondary p-2 me-2">
                    <FaFileAlt className="text-white" size={12} />
                  </div>
                  <Badge color="secondary" className="fw-bold">
                    POSTPAID ORDERS
                  </Badge>
                </div>
                <ul className="small mb-0 ps-3">
                  <li>Check postpaid agreement</li>
                  <li>Verify company documentation</li>
                  <li>Confirm with finance team</li>
                  <li>✅ Approve if agreement valid</li>
                </ul>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Quick Reference */}
        <Card className="mt-3 bg-light border-0">
          <CardBody className="p-2">
            <div className="d-flex align-items-center justify-content-center flex-wrap gap-3">
              <div className="d-flex align-items-center">
                <div className="bg-success rounded-circle me-1" style={{ width: "8px", height: "8px" }}></div>
                <small className="text-muted">FREE = Approve</small>
              </div>
              <div className="d-flex align-items-center">
                <div className="bg-info rounded-circle me-1" style={{ width: "8px", height: "8px" }}></div>
                <small className="text-muted">PAID = Verify & Approve</small>
              </div>
              <div className="d-flex align-items-center">
                <div className="bg-warning rounded-circle me-1" style={{ width: "8px", height: "8px" }}></div>
                <small className="text-muted">PENDING = Don't Approve</small>
              </div>
              <div className="d-flex align-items-center">
                <div className="bg-secondary rounded-circle me-1" style={{ width: "8px", height: "8px" }}></div>
                <small className="text-muted">POSTPAID = Check Agreement</small>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Footer */}
        <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
          
          <Button color="primary" size="sm" onClick={toggle}>
            Got it!
          </Button>
        </div>
      </ModalBody>

      <style>{`
        .verification-guide-modal .modal-dialog {
          margin: 2rem auto;
        }
        
        .verification-guide-modal .modal-content {
          border-radius: 12px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.2);
        }
        
        .verification-guide-modal .card {
          transition: transform 0.2s ease;
        }
        
        .verification-guide-modal .card:hover {
          transform: translateY(-2px);
        }
      `}</style>
    </Modal>
  )
}

export default VerificationGuideModal
